import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { formatCreatedDate } from '../../../constants/utils';
import { WorkOrderStatusBadge, PriorityBadge, WorkTypeBadge } from '../Badges';
import { useTranslation } from 'react-i18next';

/**
 * Shared component for displaying work orders in a table
 *
 * @param {Array} data - Array of work order objects
 * @param {Array} columns - Array of column keys to display
 * @param {Function} onTicketClick - Handler when ticket number is clicked
 * @param {Function} onAssetClick - Handler when asset is clicked (optional)
 * @param {Function} onLocationClick - Handler when location is clicked (optional)
 * @param {Function} onReportedByClick - Handler when reported by is clicked (optional)
 * @param {boolean} showRowNumbers - Show row numbers (default: true)
 * @param {string} emptyMessage - Message when no data (default: "No work orders found")
 * @param {string} emptyIcon - Icon for empty state (default: "inbox")
 * @param {boolean} animated - Enable row animations (default: true)
 *
 * Supported columns:
 * - ticketNumber: Clickable ticket number link
 * - title: Work order title
 * - status: Status badge (WorkOrderStatusBadge)
 * - priority: Priority badge (PriorityBadge)
 * - workType: Work type badge (WorkTypeBadge)
 * - asset: Asset name (clickable if onAssetClick provided)
 * - location: Location name (clickable if onLocationClick provided)
 * - reportedBy: Reporter name (clickable if onReportedByClick provided)
 * - createdAt: Created date
 * - dueDate: Due date
 * - resolvedAt: Resolved date
 */

const WorkOrdersTable = ({
  data = [],
  columns = ['ticketNumber', 'title', 'status', 'priority', 'createdAt'],
  onTicketClick,
  onAssetClick,
  onLocationClick,
  onReportedByClick,
  showRowNumbers = true,
  emptyMessage = 'No work orders found',
  emptyIcon = 'inbox',
  animated = true,
}) => {
  const { t } = useTranslation();

  const COLUMN_CONFIG = {
    ticketNumber: { label: t('shared.ticket_number', 'Ticket Number'), key: 'ticketNumber', altKey: 'woNumber' },
    title: { label: t('shared.title', 'Title'), key: 'title', altKey: 'subject' },
    status: { label: t('shared.status', 'Status'), key: 'status' },
    priority: { label: t('shared.priority', 'Priority'), key: 'priority', altKey: 'urgencyLevel' },
    workType: { label: t('shared.work_type', 'Work Type'), key: 'workType' },
    asset: { label: t('shared.asset', 'Asset'), key: 'asset' },
    location: { label: t('shared.location', 'Location'), key: 'location' },
    reportedBy: { label: t('shared.reported_by', 'Reported By'), key: 'reportedBy' },
    createdAt: { label: t('shared.created', 'Created'), key: 'createdAt' },
    dueDate: { label: t('shared.due_date', 'Due Date'), key: 'dueDate', altKey: 'requestDate' },
    resolvedAt: { label: t('shared.resolved', 'Resolved'), key: 'resolvedAt' },
  };

  const displayEmptyMessage = emptyMessage === 'No work orders found' ? t('shared.no_work_orders_found', 'No work orders found') : emptyMessage;

  // Get value from work order with fallback to alternate key
  const getValue = (wo, columnKey) => {
    const config = COLUMN_CONFIG[columnKey];
    if (!config) return null;

    let value = wo[config.key];
    if ((value === null || value === undefined) && config.altKey) {
      value = wo[config.altKey];
    }
    return value;
  };

  // Render cell based on column type
  const renderCell = (wo, columnKey, index) => {
    const value = getValue(wo, columnKey);

    switch (columnKey) {
      case 'ticketNumber':
        return (
          <span
            className="text-primary fw-medium"
            onClick={() => onTicketClick?.(value, wo)}
            style={{ cursor: onTicketClick ? 'pointer' : 'default' }}
          >
            {value || '-'}
          </span>
        );

      case 'title':
        return (
          <span
            className="fw-semibold text-primary cursor-pointer hover-underline text-truncate d-inline-block"
            onClick={() => onTicketClick?.(getValue(wo, 'ticketNumber'), wo)}
            style={{ cursor: onTicketClick ? 'pointer' : 'default', maxWidth: '200px' }}
            title={value || ''}
          >
            {value || '-'}
          </span>
        );

      case 'status':
        return value ? <WorkOrderStatusBadge status={value} /> : <span className="text-muted">-</span>;

      case 'priority':
        return value ? <PriorityBadge priority={value} /> : <span className="text-muted">-</span>;

      case 'workType':
        return value ? <WorkTypeBadge workType={value} /> : <span className="text-muted">-</span>;

      case 'asset':
        const assetName = wo.asset?.assetName || wo.assetName || value?.assetName || value;
        if (!assetName) return <span className="text-muted">-</span>;
        return (
          <span
            className={onAssetClick ? 'text-primary' : ''}
            onClick={() => onAssetClick?.(wo.asset || wo)}
            style={{ cursor: onAssetClick ? 'pointer' : 'default' }}
          >
            {assetName}
          </span>
        );

      case 'location':
        const locationName = wo.location?.name || wo.locationName || value?.name || value;
        if (!locationName) return <span className="text-muted">-</span>;
        return (
          <span
            className={onLocationClick ? 'text-primary' : ''}
            onClick={() => onLocationClick?.(wo.location || wo)}
            style={{ cursor: onLocationClick ? 'pointer' : 'default' }}
          >
            {locationName}
          </span>
        );

      case 'reportedBy':
        const reporterName = wo.reportedBy?.name || wo.reportedByName || value?.name || value;
        if (!reporterName) return <span className="text-muted">-</span>;
        return (
          <span
            className={onReportedByClick ? 'text-primary' : ''}
            onClick={() => onReportedByClick?.(wo.reportedBy || wo)}
            style={{ cursor: onReportedByClick ? 'pointer' : 'default' }}
          >
            {reporterName}
          </span>
        );

      case 'createdAt':
      case 'dueDate':
      case 'resolvedAt':
        return value ? formatCreatedDate(value) : <span className="text-muted">-</span>;

      default:
        return value || <span className="text-muted">-</span>;
    }
  };

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-5">
        <div
          className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
          style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}
        >
          <FeatherIcon icon={emptyIcon} size={28} className="text-muted" />
        </div>
        <p className="text-muted mb-0">{displayEmptyMessage}</p>
      </div>
    );
  }

  const RowWrapper = animated ? motion.tr : 'tr';
  const rowProps = animated
    ? {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -20 },
        transition: { duration: 0.3 },
      }
    : {};

  return (
    <div className="table-responsive">
      <table className="table table-hover mb-0">
        <thead className="table-light">
          <tr>
            {showRowNumbers && <th style={{ width: '50px' }}>#</th>}
            {columns.map((col) => (
              <th key={col}>{COLUMN_CONFIG[col]?.label || col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {animated ? (
            <AnimatePresence>
              {data.map((wo, index) => (
                <RowWrapper key={wo.id || index} {...rowProps}>
                  {showRowNumbers && <td>{index + 1}</td>}
                  {columns.map((col) => (
                    <td key={col}>{renderCell(wo, col, index)}</td>
                  ))}
                </RowWrapper>
              ))}
            </AnimatePresence>
          ) : (
            data.map((wo, index) => (
              <tr key={wo.id || index}>
                {showRowNumbers && <td>{index + 1}</td>}
                {columns.map((col) => (
                  <td key={col}>{renderCell(wo, col, index)}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default WorkOrdersTable;
